import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { api, setToken, errText } from '@/lib/api';
import { RoleSwitcher } from './RoleSwitcher';
import { MfaDialog } from './MfaDialog';
import { HelpBubble } from './HelpBubble';
import { AssistantPanel } from './AssistantPanel';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Activity, Layers, Users, Settings, Receipt, FileText, FileSearch, ListChecks, Stethoscope, ClipboardList, ShieldCheck, Menu, LogOut, ChevronDown, Zap, Sparkles, KeyRound, MonitorOff, Loader2, Building2, Crown, LifeBuoy, Lightbulb, Network, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function navByRole(role) {
  switch (role) {
    case 'doctor':
      return [
        { to: '/doctor', label: 'Review Queue', icon: ListChecks },
        { to: '/doctor/fast-sign', label: 'Fast-Sign', icon: Zap },
        { to: '/doctor/archive', label: 'Records Archive', icon: FileSearch },
      ];
    case 'nurse':
      return [
        { to: '/nurse', label: 'Submission Queue', icon: ClipboardList },
        { to: '/nurse/fast-review', label: 'Fast Review', icon: Sparkles },
        { to: '/nurse/archive', label: 'Records Archive', icon: FileSearch },
      ];
    case 'admin':
      return [
        { to: '/admin', label: 'Dashboard', icon: Activity },
        { to: '/admin/catalog', label: 'Procedure Catalog', icon: Layers },
        { to: '/admin/users', label: 'Users', icon: Users },
        { to: '/admin/billing', label: 'Billing', icon: Receipt },
        { to: '/admin/audit', label: 'Audit Logs', icon: FileText },
        { to: '/admin/settings', label: 'Settings', icon: Settings },
      ];
    case 'owner':
      return [
        { to: '/owner', label: 'Overview', icon: Activity },
        { to: '/owner/practices', label: 'Practices', icon: Building2 },
        // A practice is a site; an organisation is the customer above it (ADR 0003).
        { to: '/owner/organisations', label: 'Organisations', icon: Network },
        { to: '/owner/support', label: 'Support Desk', icon: LifeBuoy },
        { to: '/owner/feedback', label: 'Feedback', icon: Lightbulb },
        { to: '/owner/audit', label: 'Platform Audit', icon: FileText },
        { to: '/owner/settings', label: 'Platform Settings', icon: Settings },
      ];
    default:
      return [];
  }
}

// `rail`: below lg the sidebar is a 44px icon rail (tablets kept losing in-app
// navigation at 768-1023px — review 2026-09-06 §3); labels return at lg.
function Sidebar({ items, rail = false }) {
  const loc = useLocation();
  return (
    <nav className={cn('flex flex-col gap-1', rail ? 'items-center p-2 lg:items-stretch lg:p-3' : 'p-3')} aria-label="sidebar">
      {items.map(({ to, label, icon: Icon }) => {
        const active = loc.pathname === to || (to !== '/' && loc.pathname.startsWith(to));
        return (
          <NavLink
            key={to}
            to={to}
            title={rail ? label : undefined}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            className={cn(
              'flex items-center gap-2 rounded-lg text-sm transition-colors',
              rail ? 'h-11 w-11 justify-center lg:h-auto lg:w-auto lg:justify-start lg:px-3 lg:py-2' : 'px-3 py-2',
              active ? 'bg-accent text-accent-foreground border-l-2 border-l-primary' : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className={rail ? 'sr-only lg:not-sr-only' : undefined}>{label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error('New passwords do not match'); return; }
    setBusy(true);
    try {
      const r = await api.post('/auth/change-password', { current_password: current, new_password: next });
      if (r.data?.token) setToken(r.data.token); // stay signed in here; other sessions are revoked
      toast.success('Password updated. Other sessions were signed out.');
      setCurrent(''); setNext(''); setConfirm('');
      onOpenChange(false);
    } catch (err) {
      toast.error(errText(err, 'Could not change the password'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>At least 10 characters with a letter and a number. All other sessions will be signed out.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cp-current" className="text-xs font-medium">Current password</Label>
            <Input id="cp-current" type="password" required autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="change-password-current" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-new" className="text-xs font-medium">New password</Label>
            <Input id="cp-new" type="password" required autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="change-password-new" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-confirm" className="text-xs font-medium">Confirm new password</Label>
            <Input id="cp-confirm" type="password" required autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="change-password-confirm" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="change-password-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AppShell({ children }) {
  const { role, user, practice, logout } = useApp();
  const navigate = useNavigate();
  // Patient requests (ADR 0008) shows only where the rail is switched on, so a
  // practice is never sent to a page that answers "not available".
  const [patientDirect, setPatientDirect] = useState(false);
  useEffect(() => {
    if (role !== 'nurse' && role !== 'admin') return undefined;
    let alive = true;
    api.get('/patient-requests/config')
      .then((r) => { if (alive) setPatientDirect(!!r.data?.enabled); })
      .catch(() => {});
    return () => { alive = false; };
  }, [role]);
  const items = patientDirect
    ? [...navByRole(role), { to: '/patient-requests', label: 'Patient requests', icon: Inbox }]
    : navByRole(role);
  const [pwOpen, setPwOpen] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const mustEnroll = !!user?.mfa_enrollment_required;

  const RoleIcon = role === 'doctor' ? Stethoscope : role === 'nurse' ? ClipboardList : role === 'owner' ? Crown : ShieldCheck;

  const signOut = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const signOutEverywhere = async () => {
    try {
      await api.post('/auth/logout-all');
      toast.success('Signed out of all devices');
    } catch (e) { /* proceed regardless */ }
    signOut();
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b">
        <div className="flex h-14 items-center gap-3 px-4 lg:px-6">
          {/* Mobile sidebar */}
          <Sheet>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <div className="p-4 border-b">
                <Link to="/" className="flex items-center gap-2"><div className="h-7 w-7 rounded-lg bg-primary text-primary-foreground grid place-items-center font-bold">M</div><span className="font-semibold">DoctorPaperwork</span></Link>
              </div>
              <Sidebar items={items} />
            </SheetContent>
          </Sheet>

          <Link to="/" className="flex items-center gap-2" data-testid="app-logo">
            <div className="h-7 w-7 rounded-lg bg-primary text-primary-foreground grid place-items-center font-bold">M</div>
            <span className="hidden sm:inline font-semibold tracking-tight whitespace-nowrap">DoctorPaperwork</span>
            <span className="hidden lg:inline ml-2 text-xs text-muted-foreground whitespace-nowrap max-w-48 truncate">{role === 'owner' ? 'Owner Console' : practice?.name}</span>
          </Link>

          <div className="flex-1" />
          {!mustEnroll && <AssistantPanel />}
          {!mustEnroll && <RoleSwitcher />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-trigger">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-2.5 py-1 text-xs">
                  <RoleIcon className="h-3.5 w-3.5" />
                  <span className="hidden xl:inline max-w-40 truncate">{user?.name || 'User'}</span>
                </span>
                <ChevronDown className="h-4 w-4 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-xs text-muted-foreground font-normal truncate">{user?.email}</div>
                <div className="text-xs text-muted-foreground font-normal capitalize mt-0.5">{role} · {role === 'owner' ? 'Platform' : practice?.name}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="user-menu-change-password">
                <KeyRound className="h-4 w-4 mr-2" /> Change password
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setMfaOpen(true)} data-testid="user-menu-mfa">
                <ShieldCheck className="h-4 w-4 mr-2" /> Two-factor authentication
              </DropdownMenuItem>
              <DropdownMenuItem onClick={signOutEverywhere} data-testid="user-menu-signout-all">
                <MonitorOff className="h-4 w-4 mr-2" /> Sign out of all devices
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut} data-testid="user-menu-signout">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
          <MfaDialog open={mfaOpen || mustEnroll} required={mustEnroll} onOpenChange={setMfaOpen} />
        </div>
      </header>

      <div className="flex flex-1">
        <aside className="hidden md:block w-16 lg:w-64 shrink-0 border-r bg-white sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto">
          <Sidebar items={items} rail />
        </aside>
        <main className="flex-1 min-w-0">
          {!mustEnroll && children}
        </main>
      </div>
      {role !== 'owner' && <HelpBubble />}
    </div>
  );
}
